import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Rocket, TrendingUp, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const AppPreviewFrame = ({ fullWidth = false }: { fullWidth?: boolean }) => (
  <div className="relative rounded-2xl overflow-hidden"
    style={{ border: "1px solid rgba(20,160,148,0.28)", boxShadow: "0 0 80px rgba(20,160,148,0.16), 0 24px 60px rgba(0,0,0,0.4)" }}>
    {/* Browser chrome */}
    <div className="flex items-center gap-1.5 px-4 py-2.5 border-b"
      style={{ background: "#061020", borderColor: "rgba(20,160,148,0.18)" }}>
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(239,68,68,0.5)" }} />
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(234,179,8,0.5)" }} />
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(20,160,148,0.6)" }} />
      <div className="ml-3 flex-1 flex items-center gap-2 bg-white/5 rounded-md px-3 py-1 max-w-xs">
        <div className="w-2 h-2 rounded-full" style={{ background: "#14a094" }} />
        <span className="text-[10px] font-mono" style={{ color: "rgba(150,180,210,0.6)" }}>app.adv3nture.io</span>
      </div>
      <a href="/investor-app.html" target="_blank" rel="noopener noreferrer"
        className="ml-auto text-[9px] flex items-center gap-1 px-2 py-1 rounded"
        style={{ color: "#5fcfc0", background: "rgba(20,160,148,0.1)", border: "1px solid rgba(20,160,148,0.2)" }}>
        <ExternalLink className="w-2.5 h-2.5" /> Full screen
      </a>
    </div>

    {/* Live iframe */}
    {fullWidth ? (
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <iframe
          src="/investor-app.html"
          title="AdV3nture app, interactive preview"
          className="absolute inset-0 w-full h-full border-0"
          style={{ background: "#0a1628" }}
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    ) : (
      <div className="relative" style={{ height: 480 }}>
        <iframe
          src="/investor-app.html"
          title="AdV3nture app, interactive preview"
          className="absolute inset-0 w-full h-full border-0"
          style={{ background: "#0a1628", transform: "scale(0.75)", transformOrigin: "top left", width: "133.33%", height: "133.33%" }}
          sandbox="allow-scripts allow-same-origin"
        />
        <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent, #0a1628)" }} />
      </div>
    )}
  </div>
);

const HeroSection = () => {
  return (
    <section
      className="relative min-h-[70vh] flex items-center overflow-hidden pt-20"
      aria-label="Hero: connect founders and investors"
    >
      {/* Background */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0" style={{ background: "#0a1628" }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-[140px]"
          style={{ background: "rgba(20,160,148,0.07)" }} />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-[100px]"
          style={{ background: "rgba(20,160,148,0.05)" }} />
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: "linear-gradient(rgba(20,160,148,1) 1px, transparent 1px), linear-gradient(90deg, rgba(20,160,148,1) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
      </div>

      <div className="container mx-auto px-6 relative z-10 py-12">
        <div className="max-w-6xl mx-auto">

          {/* Top: copy */}
          <div className="mb-4">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-8"
              style={{ border: "1px solid rgba(20,160,148,0.35)", background: "rgba(20,160,148,0.1)" }}
            >
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#14a094", boxShadow: "0 0 8px #14a094" }} />
              <span className="text-sm font-medium" style={{ color: "#5fcfc0" }}>Now in Early Access</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
              className="text-5xl sm:text-6xl font-display font-bold leading-[1.1] mb-6"
              style={{ color: "#e6ecf5" }}
            >
              Stop pitching blind.{" "}
              <span className="text-gradient">Start getting funded.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="text-lg mb-10 leading-relaxed max-w-lg"
              style={{ color: "rgba(150,180,210,0.75)" }}
            >
              We match early-stage founders with investors who actually fit, by sector, stage, and geography. No cold emails, no LinkedIn spam. Just real conversations with people who want to back you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row items-start gap-4"
            >
              <Button asChild size="lg" className="text-base px-8 py-6 gap-2 font-semibold"
                style={{ background: "#14a094", color: "#061020", boxShadow: "0 0 40px rgba(20,160,148,0.4)" }}>
                <Link to="/for-founders">
                  <Rocket className="w-4 h-4" /> I'm a Founder
                </Link>
              </Button>
              <Button asChild size="lg" className="text-base px-8 py-6 gap-2 font-semibold"
                style={{ background: "transparent", border: "1px solid rgba(95,207,192,0.4)", color: "#5fcfc0", boxShadow: "0 0 30px rgba(20,160,148,0.12)" }}>
                <Link to="/for-investors">
                  <TrendingUp className="w-4 h-4" /> I'm an Investor
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
              className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10"
            >
              {["UK Incorporated", "KYC Verified", "Free to Join"].map((tag) => (
                <span key={tag} className="flex items-center gap-1.5 text-sm" style={{ color: "rgba(150,180,210,0.55)" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#14a094", boxShadow: "0 0 5px #14a094" }} />
                  {tag}
                </span>
              ))}
            </motion.div>
          </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
